import { useEffect, useRef, useState } from 'react'
import { useChatStore } from '@/store/chatStore'
import { UserMessage } from '@/components/message/UserMessage'
import { AssistantMessage } from '@/components/message/AssistantMessage'

// ─────────────────────────────────────────────────────────────────────────────
// ChatMessages
// Scrollable message list.
// Auto-scrolls to bottom on every new token while streaming — stays pinned
// to bottom unless user manually scrolls up.
// Shows a scroll-to-bottom arrow when user has scrolled up.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  onSend: (message: string) => void
  isConnected: boolean
}

export function ChatMessages({ onSend, isConnected }: Props) {
  const messages               = useChatStore((s) => s.messages)
  const isLoadingHistory       = useChatStore((s) => s.isLoadingHistory)
  const introMessage           = useChatStore((s) => s.introMessage)
  const suggestedQuestions     = useChatStore((s) => s.suggestedQuestions)
  const followUpSuggestions    = useChatStore((s) => s.followUpSuggestions)
  const isStreaming             = useChatStore((s) => s.isStreaming)
  const hasAppointmentSchedule = useChatStore((s) => s.hasAppointmentSchedule)
  const bookingStep            = useChatStore((s) => s.bookingStep)
  const startBooking           = useChatStore((s) => s.startBooking)
  const bottomRef        = useRef<HTMLDivElement>(null)
  const containerRef     = useRef<HTMLDivElement>(null)
  const userScrolledUp   = useRef(false)
  const [showScrollBtn, setShowScrollBtn] = useState(false)

  // Track if user manually scrolled up — stop auto-scroll if so
  function handleScroll() {
    const el = containerRef.current
    if (!el) return
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
    userScrolledUp.current = distanceFromBottom > 80
    setShowScrollBtn(distanceFromBottom > 80)
  }

  // Auto-scroll to bottom on new messages/tokens
  useEffect(() => {
    if (!userScrolledUp.current) {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages])

  function scrollToBottom() {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    userScrolledUp.current = false
    setShowScrollBtn(false)
  }

  if (isLoadingHistory) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <span className="text-sm text-[var(--color-text-sub)]">
          Loading messages...
        </span>
      </div>
    )
  }

  if (messages.length === 0) {
    const showQuestions   = suggestedQuestions.length > 0
    const showBookBtn     = hasAppointmentSchedule && bookingStep === null

    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-2.5 px-5">
        {/* Avatar */}
        <div className="
          w-11 h-11 rounded-full shrink-0
          bg-[var(--color-brand)] flex items-center justify-center
        ">
          <span className="text-white text-lg font-bold">D</span>
        </div>

        {/* Intro */}
        <p className="text-sm text-[var(--color-text-sub)] text-center leading-snug">
          {introMessage ?? 'Ask me anything about your documents.'}
        </p>

        {/* Suggested questions (max 4) + Book button — all in one column, no scroll */}
        {(showQuestions || showBookBtn) && (
          <div className="flex flex-col gap-1.5 w-full">
            {suggestedQuestions.slice(0, 4).map((q, i) => (
              <button
                key={i}
                onClick={() => isConnected && onSend(q)}
                disabled={!isConnected}
                className="
                  text-left text-xs px-3 py-1.5 rounded-xl
                  border border-[var(--color-border)]
                  bg-[var(--color-surface)]
                  text-[var(--color-text-sub)]
                  hover:border-[var(--color-brand)]
                  hover:text-[var(--color-text)]
                  transition-colors leading-snug
                  disabled:opacity-40 disabled:cursor-not-allowed
                  disabled:hover:border-[var(--color-border)]
                  disabled:hover:text-[var(--color-text-sub)]
                "
              >
                {q}
              </button>
            ))}

            {/* Book an Appointment — full-width CTA, visually distinct from questions.
                NOTE: the WhatsApp button is deliberately NOT offered here. In the empty
                state there is no conversation yet, so a handoff would reach the business
                with no question and no transcript — which is exactly the contentless
                enquiry we saw in production. WhatsApp appears only after the AI has
                replied (see AssistantMessage), so it reads as "the AI couldn't fully
                help me" and every handoff carries context. Booking needs no such
                context, so it stays. */}
            {showBookBtn && (
              <button
                onClick={startBooking}
                className="
                  w-full flex items-center justify-center gap-1.5
                  text-xs px-3 py-2 rounded-xl mt-0.5
                  border border-[var(--color-brand)]
                  text-[var(--color-brand)]
                  hover:bg-[var(--color-brand)] hover:text-white
                  transition-colors font-medium
                "
              >
                📅 Book an Appointment
              </button>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="absolute inset-0 overflow-hidden">
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="absolute inset-0 overflow-y-auto px-4 py-4"
      >
        {messages.map((message) =>
          message.role === 'user'
            ? <UserMessage key={message.id} message={message} />
            : <AssistantMessage key={message.id} message={message} />
        )}

        {/* Follow-up suggestion chips — shown after AI finishes answering */}
        {!isStreaming && followUpSuggestions.length > 0 && (
          <div className="flex flex-col gap-1.5 mt-2 mb-1 pl-9">
            {followUpSuggestions.map((q, i) => (
              <button
                key={i}
                onClick={() => isConnected && onSend(q)}
                disabled={!isConnected}
                className="
                  text-left text-xs px-3 py-1.5 rounded-xl
                  border border-[var(--color-border)]
                  bg-[var(--color-surface)]
                  text-[var(--color-text-sub)]
                  hover:border-[var(--color-brand)]
                  hover:text-[var(--color-text)]
                  transition-colors leading-snug
                  disabled:opacity-40 disabled:cursor-not-allowed
                  disabled:hover:border-[var(--color-border)]
                  disabled:hover:text-[var(--color-text-sub)]
                "
              >
                ↳ {q}
              </button>
            ))}
          </div>
        )}

        <div ref={bottomRef} />
      </div>

      {/* Scroll-to-bottom button — appears when user scrolls up */}
      {showScrollBtn && (
        <button
          onClick={scrollToBottom}
          style={{
            position: 'absolute',
            bottom: '12px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: '32px',
            height: '32px',
            borderRadius: '50%',
            background: 'var(--color-brand)',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
            zIndex: 10,
            animation: 'bounceDown 1.2s ease-in-out infinite',
          }}
          aria-label="Scroll to bottom"
        >
          {/* Down arrow SVG */}
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
            fill="white" style={{ width: 16, height: 16 }}>
            <path fillRule="evenodd"
              d="M12 3.75a.75.75 0 01.75.75v13.19l5.47-5.47a.75.75 0 111.06 1.06l-6.75 6.75a.75.75 0 01-1.06 0l-6.75-6.75a.75.75 0 111.06-1.06l5.47 5.47V4.5a.75.75 0 01.75-.75z"
              clipRule="evenodd" />
          </svg>
        </button>
      )}

      {/* Bounce keyframe — injected once */}
      <style>{`
        @keyframes bounceDown {
          0%, 100% { transform: translateX(-50%) translateY(0); }
          50%       { transform: translateX(-50%) translateY(4px); }
        }
      `}</style>
    </div>
  )
}